/**
 * Severity band / colour resolver for exception counts.
 *
 * Mirrors `backend/shared/exceptions/severity.py`. Thresholds come from the
 * `exception_types` table and are emitted per-type by the admin API as
 * `exception_metadata[domain][code].thresholds`.
 *
 * Use this instead of hardcoded `count <= 9 → orange` rules so new exception
 * types automatically pick up their DB-configured thresholds.
 */

export type SeverityBand = 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured'

export interface Thresholds {
  good: number | null
  watch: number | null
  poor: number | null
}

export interface Severity {
  band: SeverityBand
  colour: string // hex
}

export const BAND_HEX: Record<SeverityBand, string> = {
  Good: '#4caf50',
  Watch: '#ff9800',
  Poor: '#f44336',
  Critical: '#000000',
  'Not Configured': '#9e9e9e',
}

/**
 * Resolve severity for a value against its type's thresholds.
 *
 * `value` is the already-normalised metric (count or rate %). If `thresholds`
 * is missing or `good` is null, the type is "Not Configured" (grey).
 */
export function resolveSeverity(
  value: number,
  thresholds: Thresholds | null | undefined,
): Severity {
  if (!thresholds || thresholds.good === null || thresholds.good === undefined) {
    return { band: 'Not Configured', colour: BAND_HEX['Not Configured'] }
  }

  let band: SeverityBand
  if (value <= thresholds.good) {
    band = 'Good'
  } else if (thresholds.watch !== null && thresholds.watch !== undefined && value <= thresholds.watch) {
    band = 'Watch'
  } else if (thresholds.poor !== null && thresholds.poor !== undefined && value <= thresholds.poor) {
    band = 'Poor'
  } else {
    band = 'Critical'
  }
  return { band, colour: BAND_HEX[band] }
}

/**
 * Shortcut when the caller only cares about the colour.
 */
export function severityColour(
  value: number,
  thresholds: Thresholds | null | undefined,
): string {
  return resolveSeverity(value, thresholds).colour
}

/**
 * Fallback for cases where we have a count but no exception-type context
 * (e.g. aggregated totals across multiple types). Uses the legacy rule:
 * 0 → green, 1-9 → orange, 10+ → red. Prefer `severityColour` where possible.
 */
export function severityColourForAggregate(count: number): string {
  if (count === 0) return BAND_HEX.Good
  if (count <= 9) return BAND_HEX.Watch
  return BAND_HEX.Poor
}
